"use client";
import { useForm, useWatch } from "react-hook-form";
import { useState } from "react";
import { ambitos } from "../constants/ambitos";
import { zodResolver } from "@/lib/zod";
import { contactFormSchema, type ContactFormData } from "../schema";
import {
	IconMail,
	IconUser,
	IconTarget,
	IconCalendar,
	IconMessage,
	IconShield,
} from "@/lib/tabler-icons";
import Link from "next/link";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FormSection } from "./FormSection";
import { FormSubmitButton } from "./FormSubmitButton";
import { FormStatusMessage } from "./FormStatusMessage";

const temporalidadOptions = [
	{ id: "hasta-3-meses", label: "Hasta 3 Meses", value: "Hasta 3 Meses" },
	{
		id: "entre-3-1-año",
		label: "Entre 3 meses y 1 año",
		value: "Entre 3 meses y 1 año",
	},
	{ id: "mas-1-año", label: "Más de 1 año", value: "Más de 1 año" },
];

const ambitoOptions = [...ambitos]
	.sort((a, b) => a.name.localeCompare(b.name))
	.map((ambito) => ({ value: ambito.name, label: ambito.name }));

const Formulario = () => {
	const form = useForm<ContactFormData>({
		resolver: zodResolver(contactFormSchema),
		defaultValues: {
			nombre: "",
			apellidos: "",
			email: "",
			tel: "",
			ambito: "",
			area: "",
			sector: "",
			temporalidad: "",
			"como-nos-conociste": "",
			descripcion: "",
			"politica-privacidad": false,
		},
	});

	const [status, setStatus] = useState<null | "success" | "error" | "loading">(
		null
	);

	const onSubmit = async (
		data: ContactFormData,
		event?: React.BaseSyntheticEvent
	) => {
		setStatus("loading");
		// Honeypot anti-spam: leemos el valor real del DOM (campo no controlado).
		const formEl = event?.target as HTMLFormElement | undefined;
		const honeypot =
			formEl?.querySelector<HTMLInputElement>('input[name="website"]')?.value ??
			"";
		try {
			const res = await fetch("/api/contact", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					...data,
					sent: new Date().toISOString(),
					website: honeypot,
				}),
			});

			if (res.ok) {
				setStatus("success");
				form.reset();
			} else {
				setStatus("error");
			}
		} catch {
			setStatus("error");
		}
	};

	const aceptaPrivacidad = useWatch({
		control: form.control,
		name: "politica-privacidad",
	});

	return (
		<div className="w-full h-full">
			{/* Header del formulario */}
			<div className="mb-8 text-center">
				<div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4">
					<IconMail className="w-8 h-8 text-primary-600" />
				</div>
				<h3 className="text-2xl font-bold text-gray-900 mb-2">Contáctanos</h3>
				<p className="text-gray-600 text-base">
					Completa el formulario y te responderemos pronto.
				</p>
			</div>

			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-6"
					aria-label="Formulario de contacto"
				>
					{/* Honeypot anti-spam: invisible para personas, los bots lo rellenan */}
					<div
						aria-hidden="true"
						className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden"
					>
						<label htmlFor="website">No rellenar este campo</label>
						<input
							type="text"
							id="website"
							name="website"
							tabIndex={-1}
							autoComplete="off"
						/>
					</div>
					{/* Información personal */}
					<FormSection
						title="Información Personal"
						icon={<IconUser className="w-5 h-5 text-primary-600" />}
					>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="nombre"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Nombre</FormLabel>
										<FormControl>
											<Input
												autoComplete="given-name"
												placeholder="Tu nombre"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="apellidos"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Apellidos</FormLabel>
										<FormControl>
											<Input
												autoComplete="family-name"
												placeholder="Tus apellidos"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Email</FormLabel>
										<FormControl>
											<Input
												type="email"
												autoComplete="email"
												placeholder="tu@email.com"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="tel"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											Teléfono{" "}
											<span className="text-ink/70 font-normal">
												(opcional)
											</span>
										</FormLabel>
										<FormControl>
											<Input
												type="tel"
												autoComplete="tel"
												placeholder="+34 123 456 789"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</FormSection>

					{/* Información del proyecto */}
					<FormSection
						title="Información del Proyecto"
						icon={<IconTarget className="w-5 h-5 text-primary-600" />}
					>
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="ambito"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Ámbito de interés</FormLabel>
										<Select
											onValueChange={field.onChange}
											value={field.value}
										>
											<FormControl>
												<SelectTrigger className="w-full">
													<SelectValue placeholder="Selecciona un ámbito" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{ambitoOptions.map((option) => (
													<SelectItem key={option.value} value={option.value}>
														{option.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<FormField
									control={form.control}
									name="area"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Área de Atención</FormLabel>
											<FormControl>
												<Input
													autoComplete="organization"
													placeholder="Tu área de trabajo"
													{...field}
												/>
											</FormControl>
											<FormDescription>Ej: Educación, Salud, etc.</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="sector"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Sector Profesional</FormLabel>
											<FormControl>
												<Input
													autoComplete="organization"
													placeholder="Tu sector profesional"
													{...field}
												/>
											</FormControl>
											<FormDescription>Ej: ONG, Empresa, etc.</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					</FormSection>

					{/* Temporalidad */}
					<FormSection
						title="Temporalidad del Proyecto"
						icon={<IconCalendar className="w-5 h-5 text-primary-600" />}
					>
						<FormField
							control={form.control}
							name="temporalidad"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<RadioGroup
											onValueChange={field.onChange}
											value={field.value}
											className="flex flex-col gap-3 sm:flex-row sm:gap-6"
										>
											{temporalidadOptions.map((option) => (
												<FormItem
													key={option.id}
													className="flex items-center gap-2 space-y-0"
												>
													<FormControl>
														<RadioGroupItem value={option.value} />
													</FormControl>
													<FormLabel className="font-normal">
														{option.label}
													</FormLabel>
												</FormItem>
											))}
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</FormSection>

					{/* Descripción del proyecto */}
					<FormSection
						title="Descripción del Proyecto"
						icon={<IconMessage className="w-5 h-5 text-primary-600" />}
					>
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="descripcion"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											Descríbenos tu situación y necesidades
										</FormLabel>
										<FormControl>
											<Textarea
												rows={4}
												maxLength={500}
												placeholder="Cuéntanos brevemente tu situación actual y lo que necesitas..."
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="como-nos-conociste"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											¿Cómo nos conociste?{" "}
											<span className="text-ink/70 font-normal">
												(opcional)
											</span>
										</FormLabel>
										<FormControl>
											<Input
												placeholder="¿Cómo llegaste a nosotros?"
												{...field}
											/>
										</FormControl>
										<FormDescription>
											Ej: Redes sociales, recomendación, etc.
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</FormSection>

					{/* Política de privacidad */}
					<FormSection
						title="Términos y Condiciones"
						icon={<IconShield className="w-5 h-5 text-primary-600" />}
					>
						<FormField
							control={form.control}
							name="politica-privacidad"
							render={({ field }) => (
								<FormItem className="flex flex-row items-start gap-3 space-y-0">
									<FormControl>
										<Checkbox
											checked={field.value}
											onCheckedChange={field.onChange}
											className="mt-0.5"
										/>
									</FormControl>
									<div className="space-y-1">
										<FormLabel className="text-sm text-gray-700 font-normal">
											Acepto la{" "}
											<Link
												href="/politica-de-privacidad"
												target="_blank"
												rel="noopener noreferrer"
												className="text-terracotta underline hover:text-terracotta/80 transition-colors"
											>
												política de privacidad
											</Link>{" "}
											y el{" "}
											<Link
												href="/aviso-legal"
												target="_blank"
												rel="noopener noreferrer"
												className="text-terracotta underline hover:text-terracotta/80 transition-colors"
											>
												aviso legal
											</Link>{" "}
											*
										</FormLabel>
										<FormMessage />
									</div>
								</FormItem>
							)}
						/>
					</FormSection>

					{/* Botones de acción */}
					<div className="text-center space-y-4">
						<div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
							<FormSubmitButton
								isLoading={status === "loading"}
								isSuccess={status === "success"}
								isError={status === "error"}
								disabled={!aceptaPrivacidad}
							/>
						</div>

						{status === "success" && (
							<FormStatusMessage
								type="success"
								message="¡Mensaje enviado correctamente! Te responderemos pronto."
							/>
						)}

						{status === "error" && (
							<FormStatusMessage
								type="error"
								message="Hubo un error al enviar el mensaje. Intenta de nuevo."
							/>
						)}
					</div>
				</form>
			</Form>
		</div>
	);
};

export default Formulario;
